import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Typography, useMediaQuery } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { DEFAULT_TOKENS } from "../../theme/brandPalette";
import markezyLogo from "../../../markezy-login-logo.png";

/**
 * Public privacy policy.
 *
 * Linked from the Google OAuth consent screen, so it must stay reachable
 * without a session and keep describing exactly what the Google integrations
 * read and store. If integrations/ starts persisting metrics or sending Google
 * data to an AI provider, this page has to change before that ships.
 *
 * Bracketed placeholders are legal details still to be filled in by SERSEO.
 *
 * On desktop body and #root are overflow:hidden (index.css), so the page is its
 * own scroll container; on mobile the document scrolls.
 */

const LAST_UPDATED = "15 de septiembre de 2026";
const CONTACT_EMAIL = "serseomarketing@gmail.com";

const GOOGLE_USER_DATA_POLICY_URL =
  "https://developers.google.com/terms/api-services-user-data-policy";
const GOOGLE_PERMISSIONS_URL = "https://myaccount.google.com/connections";

// Same palette as the login screen (AuthLayout), but fixed: this page never takes the agency's branding.
const PINK = "#ef377f";
const INK = "#0f172a";
const BODY = "#334155";
const MUTED = "#64748b";
const RULE = "#e2e8f0";
const GRADIENT = `linear-gradient(95deg, ${DEFAULT_TOKENS.pink} 0%, ${DEFAULT_TOKENS.orange} 100%)`;
const GRADIENT_VERTICAL = `linear-gradient(180deg, ${DEFAULT_TOKENS.pink} 0%, ${DEFAULT_TOKENS.orange} 100%)`;

const SECTIONS = [
  { id: "quienes-somos", title: "Quiénes somos" },
  { id: "datos", title: "Datos que tratamos" },
  { id: "google", title: "Datos de Google" },
  { id: "finalidades", title: "Finalidades y base legal" },
  { id: "proveedores", title: "Proveedores" },
  { id: "conservacion", title: "Conservación" },
  { id: "seguridad", title: "Seguridad" },
  { id: "derechos", title: "Tus derechos" },
  { id: "cambios", title: "Cambios en esta política" },
];

const SCOPES = [
  {
    name: "Google Analytics",
    scope: "analytics.readonly",
    purpose:
      "Listar las cuentas y propiedades de GA4 de la cuenta conectada y consultar sus informes: sesiones, usuarios, canales, páginas y eventos clave.",
  },
  {
    name: "Google Search Console",
    scope: "webmasters.readonly",
    purpose:
      "Listar los sitios verificados y consultar su rendimiento de búsqueda: clics, impresiones, CTR, posición media y páginas.",
  },
  {
    name: "Identidad básica",
    scope: "openid, userinfo.email",
    purpose: "Identificar qué cuenta de Google se ha conectado y mostrar su email.",
  },
];

const ExternalLink = ({ href, children }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener"
    sx={{ color: PINK, textDecorationColor: "rgba(239, 55, 127, 0.4)" }}
  >
    {children}
  </Link>
);

ExternalLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node,
};

function Section({ id, number, title, children }) {
  return (
    <Box
      component="section"
      id={id}
      data-section={id}
      sx={{
        scrollMarginTop: 96,
        borderTop: `1px solid ${RULE}`,
        pt: { xs: 4, md: 5 },
        pb: { xs: 2, md: 3 },
      }}
    >
      <Typography
        component="h2"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.75,
          fontWeight: 700,
          fontSize: { xs: 22, md: 26 },
          letterSpacing: "-0.01em",
          color: INK,
          mb: 2.5,
        }}
      >
        <Box
          component="span"
          sx={{
            flex: "0 0 auto",
            width: 34,
            height: 34,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            backgroundColor: "#fdf2f8",
            border: "1px solid #fbcfe8",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0,
            color: PINK,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {String(number).padStart(2, "0")}
        </Box>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

Section.propTypes = {
  id: PropTypes.string.isRequired,
  number: PropTypes.number.isRequired,
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
};

function P({ children }) {
  return (
    <Typography sx={{ fontSize: 16, color: BODY, lineHeight: 1.75, mb: 2 }}>
      {children}
    </Typography>
  );
}

P.propTypes = { children: PropTypes.node };

function H3({ children }) {
  return (
    <Typography
      component="h3"
      sx={{ fontWeight: 700, fontSize: 17, color: INK, mt: 4, mb: 1.5 }}
    >
      {children}
    </Typography>
  );
}

H3.propTypes = { children: PropTypes.node };

function List({ items }) {
  return (
    <Box
      component="ul"
      sx={{
        pl: 2.5,
        my: 2,
        // The global CSS reset strips list styles.
        listStyleType: "disc",
        "& li::marker": { color: PINK },
      }}
    >
      {items.map((item, index) => (
        <Typography
          key={index}
          component="li"
          sx={{ fontSize: 16, color: BODY, lineHeight: 1.75, mb: 1, pl: 0.5 }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
}

List.propTypes = { items: PropTypes.arrayOf(PropTypes.node).isRequired };

export function PrivacyPolicy() {
  const scrollRef = useRef(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  // Highlight in the index whichever section is crossing the upper third.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || typeof IntersectionObserver === "undefined") return undefined;
    const root = isMobile ? null : container;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) setActiveId(visible.target.dataset.section);
      },
      { root, rootMargin: "-20% 0px -70% 0px" }
    );
    container.querySelectorAll("[data-section]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isMobile]);

  const goTo = (id) => (event) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box
      ref={scrollRef}
      sx={{
        height: { xs: "auto", md: "100vh" },
        minHeight: { xs: "100dvh", md: "auto" },
        overflowY: { xs: "visible", md: "auto" },
        backgroundColor: "#ffffff",
      }}
    >
      <Box sx={{ height: 3, backgroundImage: GRADIENT }} />

      {/* Top bar */}
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${RULE}`,
        }}
      >
        <Box
          sx={{
            maxWidth: 1120,
            mx: "auto",
            px: { xs: 2.5, md: 4 },
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Link component={RouterLink} to="/login" sx={{ display: "flex" }}>
            <Box
              component="img"
              src={markezyLogo}
              alt="Markezy — Tu agencia IA Ready. En un solo lugar"
              sx={{ height: { xs: 28, md: 32 }, width: "auto" }}
            />
          </Link>
          <Link
            component={RouterLink}
            to="/login"
            underline="none"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: 14,
              fontWeight: 500,
              color: MUTED,
              "&:hover": { color: INK },
            }}
          >
            <ArrowLeft size={16} />
            Volver a Markezy
          </Link>
        </Box>
      </Box>

      {/* Title */}
      <Box
        sx={{
          maxWidth: 1120,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          pt: { xs: 6, md: 10 },
          pb: { xs: 5, md: 7 },
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: PINK,
          }}
        >
          Legal
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 1.5,
            fontWeight: 800,
            fontSize: { xs: 36, md: 56 },
            lineHeight: 1.05,
            letterSpacing: "-0.03em",
            color: INK,
          }}
        >
          Política de{" "}
          <Box
            component="span"
            sx={{
              backgroundImage: GRADIENT,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            privacidad
          </Box>
        </Typography>
        <Typography
          sx={{
            mt: 2.5,
            maxWidth: 620,
            fontSize: { xs: 17, md: 19 },
            lineHeight: 1.6,
            color: MUTED,
          }}
        >
          Cómo tratamos tus datos en Markezy y, en particular, qué hacemos (y qué no)
          con la información de Google Analytics y Google Search Console.
        </Typography>
        <Typography sx={{ mt: 3, fontSize: 14, color: MUTED }}>
          Última actualización: {LAST_UPDATED}
        </Typography>
      </Box>

      {/* Index + content */}
      <Box
        sx={{
          maxWidth: 1120,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          pb: { xs: 6, md: 10 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "220px minmax(0, 720px)" },
          columnGap: { md: 8 },
          alignItems: "start",
        }}
      >
        <Box
          component="nav"
          aria-label="Índice"
          sx={{
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: 96,
            pt: 5,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: MUTED,
              mb: 1.5,
            }}
          >
            En esta página
          </Typography>
          {SECTIONS.map((section, index) => {
            const active = section.id === activeId;
            return (
              <Link
                key={section.id}
                href={`#${section.id}`}
                onClick={goTo(section.id)}
                underline="none"
                sx={{
                  display: "flex",
                  gap: 1.25,
                  py: 0.9,
                  pl: 1.5,
                  fontSize: 14,
                  fontWeight: active ? 700 : 500,
                  color: active ? PINK : MUTED,
                  borderLeft: "2px solid",
                  borderColor: active ? PINK : RULE,
                  transition: "all 0.2s",
                  "&:hover": { color: PINK },
                }}
              >
                <Box component="span" sx={{ opacity: 0.6, minWidth: 16 }}>
                  {index + 1}.
                </Box>
                {section.title}
              </Link>
            );
          })}
        </Box>

        <Box component="article" sx={{ minWidth: 0 }}>
          <Section id="quienes-somos" number={1} title="Quiénes somos">
            <P>
              Markezy es una plataforma de marketing digital para cualquier agencia
              de marketing, accesible en https://app.markezy.es. El responsable del
              tratamiento de los datos personales es SERSEO INBOUND MARKETING SL,
              sociedad limitada constituida el 8 de marzo de 2017, con CIF B39847330 y
              domicilio social en Calle Hernán Cortés 31, 2º, 39003 Santander
              (Cantabria), cuya actividad principal es la prestación de servicios de
              marketing digital, consultoría y tecnologías de la información.
            </P>
            <P>
              Para cualquier cuestión sobre privacidad puedes escribirnos a serseomarketing@gmail.com.
            </P>
          </Section>

          <Section id="datos" number={2} title="Datos que tratamos">
            <P>Según cómo uses Markezy, tratamos:</P>
            <List
              items={[
                "Datos de cuenta: nombre, email, rol, agencia y credenciales de acceso (la contraseña se guarda cifrada).",
                "Datos de clientes y proyectos que las agencias introducen en la plataforma: datos de contacto, sitio web, proyectos, tareas, tickets, mensajes y archivos adjuntos.",
                "Contenido que generas o envías al asistente de IA (COWORKER IA) y los resultados que produce.",
                "Datos técnicos: registros de acceso, dirección IP y datos del navegador necesarios para operar y proteger el servicio.",
                "Datos obtenidos de servicios de Google cuando conectas una cuenta de Google, descritos en detalle en el apartado 3.",
              ]}
            />
          </Section>

          <Section id="google" number={3} title="Datos de Google">
            <P>
              Markezy permite a las agencias conectar una cuenta de Google para
              mostrar las métricas de Google Analytics 4 y Google Search Console de
              sus clientes. La conexión se hace mediante OAuth de Google y siempre la
              inicia un usuario de la agencia.
            </P>

            <Box
              sx={{
                my: 3.5,
                pl: 3,
                borderLeft: "3px solid",
                borderImage: `${GRADIENT_VERTICAL} 1`,
              }}
            >
              <Typography sx={{ fontSize: 17, color: INK, lineHeight: 1.7 }}>
                En resumen: el acceso es <strong>solo de lectura</strong>, las
                métricas <strong>no se guardan</strong>, los datos{" "}
                <strong>nunca se venden ni se usan para publicidad o IA</strong>, y
                puedes <strong>revocar el acceso</strong> cuando quieras.
              </Typography>
            </Box>

            <H3>3.1 Permisos que solicitamos</H3>
            <Box sx={{ overflowX: "auto", my: 2 }}>
              <Box
                component="table"
                sx={{
                  width: "100%",
                  minWidth: 520,
                  borderCollapse: "collapse",
                  fontSize: 15,
                  "& th": {
                    textAlign: "left",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: MUTED,
                    pb: 1.25,
                    borderBottom: `1px solid ${INK}`,
                  },
                  "& td": {
                    verticalAlign: "top",
                    py: 1.75,
                    pr: 2,
                    borderBottom: `1px solid ${RULE}`,
                    color: BODY,
                    lineHeight: 1.6,
                  },
                }}
              >
                <thead>
                  <tr>
                    <th>Servicio</th>
                    <th>Permiso</th>
                    <th>Para qué</th>
                  </tr>
                </thead>
                <tbody>
                  {SCOPES.map((row) => (
                    <tr key={row.scope}>
                      <Box component="td" sx={{ fontWeight: 600, color: `${INK} !important`, whiteSpace: "nowrap" }}>
                        {row.name}
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: 13,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.scope}
                      </Box>
                      <td>{row.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            <P>
              Todos los permisos son de solo lectura. Markezy no puede modificar,
              crear ni eliminar nada en tus cuentas de Google.
            </P>

            <H3>3.2 Qué hacemos con esos datos</H3>
            <P>
              Usamos los datos de Google únicamente para mostrar, dentro de Markezy,
              los paneles de tráfico y visibilidad del cliente al que se ha vinculado
              la propiedad o el sitio, a los usuarios autorizados de su agencia.
            </P>
            <P>
              Las métricas se consultan a Google en tiempo real cada vez que se abre
              un panel y no se almacenan en nuestros servidores.
            </P>

            <H3>3.3 Qué almacenamos</H3>
            <List
              items={[
                "El identificador y el email de la cuenta de Google conectada.",
                "Los tokens de acceso y de actualización de OAuth, cifrados en la base de datos.",
                "El vínculo entre cada cliente y su propiedad de GA4 (identificador y nombre de la cuenta y la propiedad) o su sitio de Search Console (URL y tipo de propiedad).",
              ]}
            />

            <H3>3.4 Lo que no hacemos</H3>
            <List
              items={[
                "No vendemos los datos de Google ni los cedemos a terceros.",
                "No los usamos para publicidad, perfiles publicitarios ni retargeting.",
                "No los enviamos a proveedores de inteligencia artificial ni los usamos para entrenar o mejorar modelos de IA, propios o de terceros.",
                "No permitimos que ninguna persona los lea salvo los usuarios autorizados de la agencia, o nuestro equipo cuando tú lo solicites expresamente para darte soporte, sea necesario por seguridad o lo exija la ley.",
              ]}
            />

            <H3>3.5 Cumplimiento de la política de Google</H3>
            <P>
              El uso que hace Markezy de la información recibida de las API de Google,
              y su transferencia a cualquier otra aplicación, se ajusta a la{" "}
              <ExternalLink href={GOOGLE_USER_DATA_POLICY_URL}>
                Política de datos de usuario de los servicios de API de Google
              </ExternalLink>
              , incluidos los requisitos de Uso Limitado.
            </P>
            <Typography
              lang="en"
              sx={{ fontSize: 15, color: MUTED, lineHeight: 1.7, fontStyle: "italic", mb: 2 }}
            >
              Markezy&apos;s use and transfer to any other app of information received
              from Google APIs will adhere to the{" "}
              <ExternalLink href={GOOGLE_USER_DATA_POLICY_URL}>
                Google API Services User Data Policy
              </ExternalLink>
              , including the Limited Use requirements.
            </Typography>

            <H3>3.6 Cómo revocar el acceso</H3>
            <List
              items={[
                <>
                  <strong>Desvincular un cliente.</strong> En la ficha del cliente,
                  abre «Conectar Google Analytics» o «Conectar Google Search Console»
                  y pulsa «Desvincular de este cliente». Markezy deja de consultar los
                  datos de ese cliente.
                </>,
                <>
                  <strong>Desconectar la cuenta de Google.</strong> En ese mismo
                  diálogo, pulsa el icono de papelera junto a la cuenta. Markezy
                  revoca el acceso en Google y elimina de inmediato los tokens
                  almacenados.
                </>,
                <>
                  <strong>Desde Google.</strong> Puedes retirar el acceso en cualquier
                  momento desde{" "}
                  <ExternalLink href={GOOGLE_PERMISSIONS_URL}>
                    la configuración de tu cuenta de Google
                  </ExternalLink>
                  . Markezy dejará de poder leer datos de inmediato.
                </>,
              ]}
            />
          </Section>

          <Section id="finalidades" number={4} title="Finalidades y base legal">
            <List
              items={[
                "Prestar el servicio contratado por la agencia (ejecución del contrato).",
                "Gestionar la seguridad, prevenir fraudes y mantener registros técnicos (interés legítimo).",
                "Enviar comunicaciones del servicio, como invitaciones, avisos y restablecimiento de contraseña (ejecución del contrato).",
                "Cumplir obligaciones legales (obligación legal).",
              ]}
            />
            <P>
              Cuando una agencia introduce en Markezy datos personales de sus propios
              clientes, la agencia actúa como responsable del tratamiento y SERSEO
              como encargado del tratamiento.
            </P>
          </Section>

          <Section id="proveedores" number={5} title="Proveedores">
            <P>
              Para operar Markezy nos apoyamos en proveedores que tratan datos por
              nuestra cuenta y bajo contrato, entre ellos: alojamiento e
              infraestructura en la nube, envío de emails transaccionales,
              proveedores de inteligencia artificial para el asistente COWORKER IA
              (que reciben solo el contenido que se envía al asistente, nunca datos de
              Google) y las herramientas que cada agencia decida conectar, como CRM o
              gestores de proyectos.
            </P>
            <P>
              Si alguno de ellos trata datos fuera del Espacio Económico Europeo, lo
              hace con garantías adecuadas, como las cláusulas contractuales tipo de
              la Comisión Europea.
            </P>
          </Section>

          <Section id="conservacion" number={6} title="Conservación">
            <P>
              Conservamos los datos mientras la cuenta o la relación contractual esté
              activa y, después, durante los plazos legales aplicables. Los tokens de
              Google se eliminan en el momento en que se desconecta la cuenta de
              Google. Las métricas de Google no se conservan porque no se almacenan.
            </P>
          </Section>

          <Section id="seguridad" number={7} title="Seguridad">
            <P>
              Aplicamos medidas técnicas y organizativas razonables: conexiones
              cifradas (HTTPS), cifrado de los tokens de terceros en la base de datos,
              control de acceso por roles y separación de datos entre agencias.
            </P>
          </Section>

          <Section id="derechos" number={8} title="Tus derechos">
            <P>
              Puedes ejercer tus derechos de acceso, rectificación, supresión,
              oposición, limitación del tratamiento y portabilidad escribiendo a{" "}
              {CONTACT_EMAIL}. Si consideras que no hemos atendido correctamente tu
              solicitud, puedes reclamar ante la Agencia Española de Protección de
              Datos (<ExternalLink href="https://www.aepd.es">www.aepd.es</ExternalLink>
              ).
            </P>
          </Section>

          <Section id="cambios" number={9} title="Cambios en esta política">
            <P>
              Si cambiamos esta política, publicaremos la nueva versión en esta página
              con su fecha de actualización. Si el cambio afecta al uso de los datos
              de Google, lo comunicaremos antes de aplicarlo.
            </P>
          </Section>
        </Box>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ borderTop: `1px solid ${RULE}` }}>
        <Box
          sx={{
            maxWidth: 1120,
            mx: "auto",
            px: { xs: 2.5, md: 4 },
            py: 4,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 2,
            fontSize: 13,
            color: MUTED,
          }}
        >
          <span>© {new Date().getFullYear()} SERSEO · Markezy</span>
          <span>Dudas sobre privacidad: {CONTACT_EMAIL}</span>
        </Box>
      </Box>
    </Box>
  );
}

export default PrivacyPolicy;
